import React from "react";

/**
 * PittsburghWater.ai — Landing Page
 * ---------------------------------
 * A simple hub that presents the three Pittsburgh water transparency
 * apps as cards (in the style of civai.org/explore).
 *
 * Self-contained: no external libraries needed.
 */

const PROJECTS = [
  {
    eyebrow: "Interactive Dashboard",
    title: "Drinking Water & Health",
    description:
      "Explore contaminant levels across Pittsburgh's water system and see how they line up with neighborhood health outcomes — with an interactive heatmap, district map, and verified public data.",
    url: "https://pittsburgh-water-health.netlify.app",
    accent: "#E8B44A",
  },
  {
    eyebrow: "Plain-English Explainer",
    title: "The Track Record",
    description:
      "Every enforcement action, consent order, and legal agreement involving Pittsburgh Water (PWSA) — from public records, one swipeable card at a time.",
    url: "https://pittsburgh-water-track-record.netlify.app",
    accent: "#6FC3D6",
  },
  {
    eyebrow: "Salary Database",
    title: "PWSA Salaries",
    description:
      "Publicly reported employee pay at Pittsburgh Water — searchable, sortable, and sourced from public records.",
    url: "https://pwsa-salaries.netlify.app",
    accent: "#9BD08B",
  },
  {
    eyebrow: "Funding Explorer",
    title: "The Funding Tracker",
    description:
      "Where the money comes from — every major state and federal infrastructure award to Pittsburgh Water, from PENNVEST loans to EPA WIFIA financing, in one place.",
    url: "https://pwsa-funding-tracker.netlify.app",
    accent: "#C9A2E8",
  },
];

function ConfluenceMark() {
  // Three streams converging — Pittsburgh's three rivers.
  return (
    <svg
      className="pw-mark"
      viewBox="0 0 120 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M4 8 C 40 8, 55 22, 116 22"
        stroke="#E8B44A"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M4 22 L 116 22"
        stroke="#6FC3D6"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path
        d="M4 36 C 40 36, 55 22, 116 22"
        stroke="#9BD08B"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ProjectCard({ project }) {
  return (
    <a
      className="pw-card"
      href={project.url}
      style={{ "--card-accent": project.accent }}
    >
      <div className="pw-card-eyebrow">{project.eyebrow}</div>
      <h2 className="pw-card-title">{project.title}</h2>
      <p className="pw-card-desc">{project.description}</p>
      <div className="pw-card-cta">
        Open the app <span className="pw-arrow">→</span>
      </div>
    </a>
  );
}

export default function App() {
  return (
    <div className="pw-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Inter:wght@400;500;600&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .pw-page {
          min-height: 100vh;
          background:
            radial-gradient(1200px 500px at 80% -10%, rgba(111,195,214,0.10), transparent 60%),
            radial-gradient(900px 420px at 10% 110%, rgba(232,180,74,0.08), transparent 60%),
            #0C1B24;
          color: #E9EEF1;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          display: flex;
          flex-direction: column;
        }

        .pw-shell {
          width: 100%;
          max-width: 1080px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* ---------- Header ---------- */
        .pw-header {
          padding: 28px 0 0;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 8px;
        }

        .pw-wordmark {
          font-family: 'Fraunces', Georgia, serif;
          font-weight: 700;
          font-size: 1.25rem;
          letter-spacing: 0.01em;
          color: #FFFFFF;
        }

        .pw-wordmark .ai { color: #E8B44A; }

        .pw-header-note {
          font-size: 0.8rem;
          color: #8FA3AD;
          letter-spacing: 0.06em;
          text-transform: uppercase;
        }

        /* ---------- Hero ---------- */
        .pw-hero {
          padding: 72px 0 48px;
          text-align: center;
        }

        .pw-mark {
          width: 120px;
          height: 44px;
          margin-bottom: 28px;
          opacity: 0.9;
        }

        .pw-hero h1 {
          font-family: 'Fraunces', Georgia, serif;
          font-weight: 600;
          font-size: clamp(2rem, 5vw, 3.1rem);
          line-height: 1.12;
          color: #FFFFFF;
          max-width: 720px;
          margin: 0 auto 20px;
        }

        .pw-hero h1 em {
          font-style: italic;
          color: #E8B44A;
        }

        .pw-hero p {
          font-size: 1.05rem;
          line-height: 1.65;
          color: #AEC0C9;
          max-width: 560px;
          margin: 0 auto;
        }

        /* ---------- Card grid ---------- */
        .pw-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 20px;
          padding: 8px 0 72px;
        }

        .pw-card {
          display: flex;
          flex-direction: column;
          gap: 14px;
          background: rgba(255, 255, 255, 0.035);
          border: 1px solid rgba(255, 255, 255, 0.09);
          border-radius: 14px;
          padding: 28px 26px 24px;
          text-decoration: none;
          color: inherit;
          transition: transform 0.18s ease, border-color 0.18s ease,
            background 0.18s ease;
        }

        .pw-card:hover,
        .pw-card:focus-visible {
          transform: translateY(-4px);
          border-color: var(--card-accent);
          background: rgba(255, 255, 255, 0.055);
          outline: none;
        }

        .pw-card:focus-visible {
          box-shadow: 0 0 0 3px rgba(232, 180, 74, 0.35);
        }

        .pw-card-eyebrow {
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--card-accent);
        }

        .pw-card-title {
          font-family: 'Fraunces', Georgia, serif;
          font-weight: 600;
          font-size: 1.45rem;
          color: #FFFFFF;
          line-height: 1.2;
        }

        .pw-card-desc {
          font-size: 0.93rem;
          line-height: 1.6;
          color: #AEC0C9;
          flex-grow: 1;
        }

        .pw-card-cta {
          font-size: 0.9rem;
          font-weight: 600;
          color: #FFFFFF;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .pw-arrow {
          transition: transform 0.18s ease;
          color: var(--card-accent);
        }

        .pw-card:hover .pw-arrow {
          transform: translateX(4px);
        }

        /* ---------- Footer ---------- */
        .pw-footer {
          margin-top: auto;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          padding: 26px 0 34px;
          text-align: center;
        }

        .pw-footer p {
          font-size: 0.82rem;
          line-height: 1.7;
          color: #7E939E;
          max-width: 640px;
          margin: 0 auto;
        }

        @media (max-width: 680px) {
          .pw-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .pw-card, .pw-arrow { transition: none; }
        }
      `}</style>

      <div className="pw-shell">
        <header className="pw-header">
          <div className="pw-wordmark">
            PittsburghWater<span className="ai">.ai</span>
          </div>
          <div className="pw-header-note">Civic Water Data</div>
        </header>

        <section className="pw-hero">
          <ConfluenceMark />
          <h1>
            Pittsburgh&rsquo;s water, <em>on the record.</em>
          </h1>
          <p>
            Four ways into the public data — what&rsquo;s in the water, how the
            utility has been held accountable, what its people are paid, and
            where the money to fix it comes from. All built from verified
            public sources.
          </p>
        </section>

        <main className="pw-grid">
          {PROJECTS.map((project) => (
            <ProjectCard key={project.title} project={project} />
          ))}
        </main>
      </div>

      <footer className="pw-footer">
        <div className="pw-shell">
          <p>
            Built from public records: PA DEP, U.S. EPA, PENNVEST, PA Office of Attorney
            General, U.S. DOJ, and pgh2o.com. Independent project — not
            affiliated with Pittsburgh Water (PWSA).
          </p>
        </div>
      </footer>
    </div>
  );
}
